import { useCallback, useEffect, useState } from 'react';
import ClientLoginPage from './pages/login/ClientLoginPage';
import KakaoCallbackPage from './pages/kakao-callback/KakaoCallbackPage';
import ClientSignupPage from './pages/signup/ClientSignupPage';
import TeamDetailPage from './pages/team-detail/TeamDetailPage';
import ClientLandingPage from './pages/landing/ClientLandingPage';
import ClientTeamCreatePage from './pages/team-create/ClientTeamCreatePage';
import ClientTeamSelectPage from './pages/team-select/ClientTeamSelectPage';
import ClientTeamFindPage from './pages/team-find/ClientTeamFindPage';
import ClientSchedulePage from './pages/schedule/ClientSchedulePage';
import ClientMyPage from './pages/my/ClientMyPage';
import ClientSettingsPage from './pages/settings/ClientSettingsPage';
import ClientTermsPage from './pages/terms/ClientTermsPage';
import ClientPrivacyPage from './pages/privacy/ClientPrivacyPage';
import ClientMarketingConsentPage from './pages/marketing-consent/ClientMarketingConsentPage';
import ClientAppShell from './components/app-shell/ClientAppShell';
import { getMyProfile, logoutWithRefreshToken } from './api/auth/authApi';
import { registerPushToken, unregisterPushToken } from './api/push/pushTokenApi';
import { getMyTeams } from './api/team/teamApi';
import {
  clearAuthTokens,
  getAccessToken,
  getRefreshToken,
  hasStoredAuthTokens,
} from './auth/tokenStorage';
import { setAuthFailureHandler } from './api/httpClient';
import {
  DEFAULT_NOTIFICATION_SETTINGS,
  NOTIFICATION_SETTINGS_CHANGED_EVENT,
  NOTIFICATION_SETTINGS_STORAGE_KEY,
  PUSH_TOKEN_SYNC_SIGNATURE_KEY,
} from './push/pushConstants';
import {
  loadStoredPushDevice,
  requestNativePushToken,
  subscribeNativePushToken,
} from './push/pushBridge';

const KAKAO_BACK_GUARD_SESSION_KEY = 'ahshootKakaoBackGuardEnabled';

function AuthLoadingPage() {
  return (
    <main className="grid min-h-screen place-items-center bg-slate-50 p-6">
      <section className="w-full max-w-[420px] rounded-2xl border border-slate-200 bg-white p-7 text-center shadow-sm">
        <h1 className="m-0 text-xl font-semibold text-slate-900">인증 상태 확인 중</h1>
        <p className="mt-2 text-sm text-slate-600">잠시만 기다려 주세요.</p>
      </section>
    </main>
  );
}

function TeamLoadingPage() {
  return (
    <main className="grid min-h-screen place-items-center bg-slate-50 p-6">
      <section className="w-full max-w-[480px] rounded-2xl border border-slate-200 bg-white p-7 text-center shadow-sm">
        <h1 className="m-0 text-xl font-semibold text-slate-900">팀 정보 확인 중</h1>
        <p className="mt-2 text-sm text-slate-600">내 팀 목록을 불러오고 있습니다.</p>
      </section>
    </main>
  );
}

function TeamLoadErrorPage({ message, onRetry }) {
  return (
    <main className="grid min-h-screen place-items-center bg-slate-50 p-6">
      <section className="w-full max-w-[520px] rounded-2xl border border-rose-200 bg-white p-7 shadow-sm">
        <h1 className="m-0 text-xl font-semibold text-slate-900">팀 정보를 불러오지 못했습니다.</h1>
        <p className="mt-2 text-sm text-rose-700">{message || '잠시 후 다시 시도해 주세요.'}</p>
        <div className="mt-5 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={onRetry}
            className="h-10 rounded-lg bg-slate-900 px-4 text-sm font-semibold text-white"
          >
            다시 시도
          </button>
        </div>
      </section>
    </main>
  );
}

function getTeamIdFromPath(pathname) {
  const match = pathname.match(/^\/team\/([^/?#]+)/);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

function loadNotificationSettings() {
  const rawValue = localStorage.getItem(NOTIFICATION_SETTINGS_STORAGE_KEY);
  if (!rawValue) {
    return DEFAULT_NOTIFICATION_SETTINGS;
  }

  try {
    const parsed = JSON.parse(rawValue);
    return {
      ...DEFAULT_NOTIFICATION_SETTINGS,
      ...parsed,
    };
  } catch (error) {
    return DEFAULT_NOTIFICATION_SETTINGS;
  }
}

function ClientApp() {
  // 인증 상태와 팀 목록 상태를 분리해 라우팅 분기를 단순화한다.
  const [authState, setAuthState] = useState(() => ({
    ready: !hasStoredAuthTokens(),
    isAuthenticated: false,
    me: null,
  }));
  const [teamState, setTeamState] = useState({
    ready: false,
    isLoading: false,
    teams: [],
    error: '',
  });
  const currentUserId = authState.me?.userId ?? authState.me?.id ?? null;

  // access/refresh 만료 등 인증 실패시 전역 세션을 정리한다.
  const handleAuthFailure = useCallback(() => {
    clearAuthTokens();
    localStorage.removeItem(PUSH_TOKEN_SYNC_SIGNATURE_KEY);
    sessionStorage.removeItem(KAKAO_BACK_GUARD_SESSION_KEY);
    setAuthState({
      ready: true,
      isAuthenticated: false,
      me: null,
    });
    setTeamState({
      ready: false,
      isLoading: false,
      teams: [],
      error: '',
    });
    if (!window.location.pathname.startsWith('/auth/login')) {
      window.location.replace('/auth/login');
    }
  }, []);

  useEffect(() => {
    setAuthFailureHandler(handleAuthFailure);
    return () => setAuthFailureHandler(null);
  }, [handleAuthFailure]);

  // 앱 최초 진입 시 저장된 토큰으로 /api/auth/me를 호출해 세션을 복원한다.
  useEffect(() => {
    let isUnmounted = false;

    const restoreSession = async () => {
      const accessToken = getAccessToken();
      const refreshToken = getRefreshToken();

      if (!accessToken || !refreshToken) {
        clearAuthTokens();
        if (!isUnmounted) {
          setAuthState({
            ready: true,
            isAuthenticated: false,
            me: null,
          });
        }
        return;
      }

      try {
        const me = await getMyProfile();
        if (!isUnmounted) {
          setAuthState({
            ready: true,
            isAuthenticated: true,
            me,
          });
        }
      } catch (error) {
        clearAuthTokens();
        if (!isUnmounted) {
          setAuthState({
            ready: true,
            isAuthenticated: false,
            me: null,
          });
        }
      }
    };

    if (!authState.ready) {
      restoreSession();
    }

    return () => {
      isUnmounted = true;
    };
  }, [authState.ready]);

  const syncPushTokenToServer = useCallback(
    async (pushDevice, { force = false } = {}) => {
      if (!authState.isAuthenticated || !currentUserId || !pushDevice?.token) {
        return;
      }

      const settings = loadNotificationSettings();
      const signature = JSON.stringify({
        userId: currentUserId,
        token: pushDevice.token,
        platform: pushDevice.platform || 'unknown',
        settings,
      });

      if (!force && localStorage.getItem(PUSH_TOKEN_SYNC_SIGNATURE_KEY) === signature) {
        return;
      }

      try {
        await registerPushToken({
          token: pushDevice.token,
          platform: pushDevice.platform || 'unknown',
          appVersion: process.env.REACT_APP_APP_VERSION || null,
          settings,
        });
        localStorage.setItem(PUSH_TOKEN_SYNC_SIGNATURE_KEY, signature);
      } catch (error) {
        // 앱 동작에는 영향 없이 백그라운드 등록 실패만 기록한다.
        console.error('푸시 토큰 등록에 실패했습니다.', error);
      }
    },
    [authState.isAuthenticated, currentUserId],
  );

  useEffect(() => {
    if (!authState.isAuthenticated || !currentUserId) {
      localStorage.removeItem(PUSH_TOKEN_SYNC_SIGNATURE_KEY);
      return undefined;
    }

    const syncStoredPushToken = () => {
      const storedPushDevice = loadStoredPushDevice();
      if (!storedPushDevice?.token) {
        return;
      }
      syncPushTokenToServer(storedPushDevice);
    };

    const unsubscribePushBridge = subscribeNativePushToken((pushDevice) => {
      syncPushTokenToServer(pushDevice, { force: true });
    });

    const handleNotificationSettingsChanged = () => {
      const storedPushDevice = loadStoredPushDevice();
      if (!storedPushDevice?.token) {
        return;
      }
      syncPushTokenToServer(storedPushDevice, { force: true });
    };

    window.addEventListener(
      NOTIFICATION_SETTINGS_CHANGED_EVENT,
      handleNotificationSettingsChanged,
    );

    syncStoredPushToken();
    requestNativePushToken();

    return () => {
      unsubscribePushBridge();
      window.removeEventListener(
        NOTIFICATION_SETTINGS_CHANGED_EVENT,
        handleNotificationSettingsChanged,
      );
    };
  }, [authState.isAuthenticated, currentUserId, syncPushTokenToServer]);

  const handleLogout = useCallback(async () => {
    const refreshToken = getRefreshToken();
    const storedPushDevice = loadStoredPushDevice();

    try {
      if (storedPushDevice?.token) {
        await unregisterPushToken(storedPushDevice.token);
      }
      if (refreshToken) {
        await logoutWithRefreshToken(refreshToken);
      }
    } catch (error) {
      // 로그아웃 요청 실패와 무관하게 프론트 세션은 정리한다.
    } finally {
      clearAuthTokens();
      localStorage.removeItem(PUSH_TOKEN_SYNC_SIGNATURE_KEY);
      sessionStorage.removeItem(KAKAO_BACK_GUARD_SESSION_KEY);
      setAuthState({
        ready: true,
        isAuthenticated: false,
        me: null,
      });
      setTeamState({
        ready: false,
        isLoading: false,
        teams: [],
        error: '',
      });
      window.location.replace('/auth/login');
    }
  }, []);

  // 회원탈퇴 이후에는 서버 로그아웃과 무관하게 클라이언트 세션을 즉시 폐기한다.
  const handleAccountDeactivated = useCallback(() => {
    clearAuthTokens();
    localStorage.removeItem(PUSH_TOKEN_SYNC_SIGNATURE_KEY);
    sessionStorage.removeItem(KAKAO_BACK_GUARD_SESSION_KEY);
    setAuthState({
      ready: true,
      isAuthenticated: false,
      me: null,
    });
    setTeamState({
      ready: false,
      isLoading: false,
      teams: [],
      error: '',
    });
    window.location.replace('/auth/login');
  }, []);

  // 마이페이지 프로필 수정 결과를 앱 전역 사용자 상태에 반영한다.
  const handleProfileUpdated = useCallback((updatedProfile) => {
    if (!updatedProfile || typeof updatedProfile !== 'object') {
      return;
    }

    const normalizedProfile = { ...updatedProfile };
    if (normalizedProfile.id != null && normalizedProfile.userId == null) {
      normalizedProfile.userId = normalizedProfile.id;
    }

    setAuthState((previous) => ({
      ...previous,
      me: {
        ...(previous.me || {}),
        ...normalizedProfile,
      },
    }));
  }, []);

  // /team 진입 시 팀 목록을 조회해 선택 페이지/팀 상세 화면 분기로 사용한다.
  const loadMyTeams = useCallback(async () => {
    setTeamState((previous) => ({
      ...previous,
      isLoading: true,
      error: '',
    }));

    try {
      const teams = await getMyTeams();
      setTeamState({
        ready: true,
        isLoading: false,
        teams,
        error: '',
      });
      return teams;
    } catch (error) {
      setTeamState({
        ready: true,
        isLoading: false,
        teams: [],
        error: error?.message || '내 팀 목록 조회에 실패했습니다.',
      });
      return [];
    }
  }, []);

  const pathname = window.location.pathname;
  const searchParams = new URLSearchParams(window.location.search);
  const hasKakaoOAuthCode = Boolean(searchParams.get('code'));
  const hasKakaoOAuthError = Boolean(searchParams.get('error'));
  const isSignupRoute = pathname.startsWith('/auth/signup');
  const isKakaoCallbackRoute = pathname.startsWith('/auth/kakao/callback');
  const isLoginRoute = pathname.startsWith('/auth/login');
  const isTeamCreateRoute = pathname === '/teams/new' || pathname.startsWith('/teams/new/');
  const isTeamFindRoute = pathname === '/teams/find' || pathname.startsWith('/teams/find/');
  const isScheduleRoute = pathname === '/schedule' || pathname.startsWith('/schedule/');
  const isMyRoute = pathname === '/my' || pathname.startsWith('/my/');
  const isSettingsRoute = pathname === '/settings' || pathname.startsWith('/settings/');
  const isTermsRoute = pathname === '/terms' || pathname.startsWith('/terms/');
  const isPrivacyRoute = pathname === '/privacy' || pathname.startsWith('/privacy/');
  const isMarketingConsentRoute =
    pathname === '/marketing-consent' || pathname.startsWith('/marketing-consent/');
  const isTeamRoute = pathname === '/team' || pathname.startsWith('/team/');
  const shouldRedirectToKakaoCallback =
    (hasKakaoOAuthCode || hasKakaoOAuthError) && !isKakaoCallbackRoute;

  const renderWithShell = useCallback(
    (pageNode) => (
      <ClientAppShell
        isAuthenticated={authState.isAuthenticated}
        currentUser={authState.me}
        pathname={pathname}
        onLogout={authState.isAuthenticated ? handleLogout : null}
      >
        {pageNode}
      </ClientAppShell>
    ),
    [authState.isAuthenticated, authState.me, handleLogout, pathname],
  );

  useEffect(() => {
    if (!authState.ready || !authState.isAuthenticated || !isTeamRoute) {
      return;
    }

    loadMyTeams();
  }, [authState.isAuthenticated, authState.ready, isTeamRoute, loadMyTeams]);

  useEffect(() => {
    if (!shouldRedirectToKakaoCallback) {
      return;
    }
    window.location.replace(`/auth/kakao/callback${window.location.search}`);
  }, [shouldRedirectToKakaoCallback]);

  useEffect(() => {
    if (!authState.ready || !authState.isAuthenticated) {
      return;
    }

    if (sessionStorage.getItem(KAKAO_BACK_GUARD_SESSION_KEY) !== '1') {
      return;
    }

    if (window.location.pathname.startsWith('/auth/')) {
      return;
    }

    const previousState = window.history.state;
    const hasGuardOnCurrentPath =
      previousState &&
      typeof previousState === 'object' &&
      previousState.ahshootBackGuard === true &&
      previousState.pathname === pathname;

    if (hasGuardOnCurrentPath) {
      return;
    }

    window.history.pushState(
      {
        ...(previousState && typeof previousState === 'object' ? previousState : {}),
        ahshootBackGuard: true,
        pathname,
      },
      '',
      window.location.href,
    );
  }, [authState.ready, authState.isAuthenticated, pathname]);

  if (shouldRedirectToKakaoCallback) {
    return null;
  }

  if (isKakaoCallbackRoute) {
    return <KakaoCallbackPage />;
  }

  if (!authState.ready) {
    return <AuthLoadingPage />;
  }

  if (isSignupRoute) {
    return renderWithShell(<ClientSignupPage />);
  }

  if (isLoginRoute) {
    if (authState.isAuthenticated) {
      window.location.replace('/team');
      return null;
    }
    return renderWithShell(<ClientLoginPage />);
  }

  if (isTeamCreateRoute) {
    if (!authState.isAuthenticated) {
      window.location.replace('/auth/login');
      return null;
    }

    return renderWithShell(<ClientTeamCreatePage currentUser={authState.me} />);
  }

  if (isTeamFindRoute) {
    return renderWithShell(<ClientTeamFindPage isAuthenticated={authState.isAuthenticated} />);
  }

  if (isScheduleRoute) {
    if (!authState.isAuthenticated) {
      window.location.replace('/auth/login');
      return null;
    }
    return renderWithShell(<ClientSchedulePage currentUser={authState.me} />);
  }

  if (isMyRoute) {
    if (!authState.isAuthenticated) {
      window.location.replace('/auth/login');
      return null;
    }
    return renderWithShell(
      <ClientMyPage
        currentUser={authState.me}
        onLogout={handleLogout}
        onAccountDeactivated={handleAccountDeactivated}
        onProfileUpdated={handleProfileUpdated}
      />,
    );
  }

  if (isSettingsRoute) {
    if (!authState.isAuthenticated) {
      window.location.replace('/auth/login');
      return null;
    }
    return renderWithShell(<ClientSettingsPage />);
  }

  if (isTermsRoute) {
    return renderWithShell(<ClientTermsPage />);
  }

  if (isPrivacyRoute) {
    return renderWithShell(<ClientPrivacyPage />);
  }

  if (isMarketingConsentRoute) {
    return renderWithShell(<ClientMarketingConsentPage />);
  }

  if (isTeamRoute) {
    if (!authState.isAuthenticated) {
      window.location.replace('/auth/login');
      return null;
    }

    if (!teamState.ready || teamState.isLoading) {
      return renderWithShell(<TeamLoadingPage />);
    }

    if (teamState.error) {
      return renderWithShell(<TeamLoadErrorPage message={teamState.error} onRetry={loadMyTeams} />);
    }

    const handleSelectTeam = (teamId) => {
      const normalizedTeamId = String(teamId);
      window.location.replace(`/team/${encodeURIComponent(normalizedTeamId)}`);
    };

    const selectedTeamId = getTeamIdFromPath(pathname);

    if (!selectedTeamId) {
      return (
        renderWithShell(
          <ClientTeamSelectPage
            teams={teamState.teams}
            currentUser={authState.me}
            onSelectTeam={handleSelectTeam}
          />,
        )
      );
    }

    const selectedTeam = teamState.teams.find((team) => team.id === String(selectedTeamId));

    if (!selectedTeam) {
      return (
        renderWithShell(
          <ClientTeamSelectPage
            teams={teamState.teams}
            currentUser={authState.me}
            onSelectTeam={handleSelectTeam}
          />,
        )
      );
    }

    return renderWithShell(
      <TeamDetailPage
        currentUser={authState.me}
        team={selectedTeam}
        myTeams={teamState.teams}
        onSelectTeam={handleSelectTeam}
        onRefreshTeams={loadMyTeams}
      />,
    );
  }

  return renderWithShell(<ClientLandingPage isAuthenticated={authState.isAuthenticated} />);
}

export default ClientApp;
